import { memo, useCallback } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTran } from "@/hooks";

const MONTHS = [
  { key: "1", label: "Jan" },
  { key: "2", label: "Feb" },
  { key: "3", label: "Mar" },
  { key: "4", label: "Apr" },
  { key: "5", label: "May" },
  { key: "6", label: "Jun" },
  { key: "7", label: "Jul" },
  { key: "8", label: "Aug" },
  { key: "9", label: "Sep" },
  { key: "10", label: "Oct" },
  { key: "11", label: "Nov" },
  { key: "12", label: "Dec" },
];

const normalizeNumberInput = (value) => {
  const raw = String(value ?? "");
  if (raw === "") return "";
  const normalized = raw.replace(/[^\d.-]/g, "").replace(/(?!^)-/g, "");
  return normalized.replace(/(\..*)\./g, "$1");
};

const handleNumberKeyDown = (event) => {
  const { key } = event;
  if (
    [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      ".",
      "-",
    ].includes(key)
  )
    return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(key.toLowerCase())
  )
    return;
  if (!/^\d$/.test(key)) event.preventDefault();
};

/**
 * Compact 4-column grid of month inputs with inline labels.
 *
 * Props:
 *   months       – { "1": value, ..., "12": value } (sparse OK)
 *   onChange     – (newMonths: object) => void
 *   inputType    – disclosure inputType
 *   unitOptions  – options for "selector" inputType
 *   disabled     – disables all inputs
 *   unit         – unit label shown after the last row
 *   className    – extra class on root
 */
const MonthlyTargetGrid = memo(
  ({
    months = {},
    onChange,
    inputType = "",
    unitOptions = [],
    disabled = false,
    unit,
    monthOptions,
    className,
  }) => {
    const { t } = useTran();
    const resolvedMonthOptions =
      Array.isArray(monthOptions) && monthOptions.length > 0
        ? monthOptions
        : MONTHS;

    const isNumeric =
      inputType !== "simpleLongText" &&
      inputType !== "selector" &&
      inputType !== "simple";

    const handleChange = useCallback(
      (key, rawVal) => {
        const value = isNumeric ? normalizeNumberInput(rawVal) : rawVal;
        onChange?.({ ...months, [key]: value });
      },
      [isNumeric, months, onChange],
    );

    return (
      <div className={cn("w-full", className)}>
        <div className="grid grid-cols-4 gap-1.5">
          {resolvedMonthOptions.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-1">
              <span className="w-12 shrink-0 text-[11px] font-medium leading-tight text-muted-foreground">
                {label}
              </span>
              {inputType === "selector" ? (
                <Select
                  value={months[key] ?? ""}
                  onValueChange={(val) => handleChange(key, val)}
                  disabled={disabled || unitOptions.length === 0}
                >
                  <SelectTrigger className="h-7 min-w-0 flex-1 text-xs">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptions.map((opt) => (
                      <SelectItem
                        key={opt.value ?? opt}
                        value={opt.value ?? opt}
                      >
                        {opt.label ?? opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <input
                  type="text"
                  inputMode={isNumeric ? "decimal" : "text"}
                  placeholder="—"
                  value={months[key] ?? ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  onKeyDown={isNumeric ? handleNumberKeyDown : undefined}
                  disabled={disabled}
                  className={cn(
                    "h-7 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs text-foreground",
                    "placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                />
              )}
            </div>
          ))}
        </div>
        {unit ? (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t("Unit")}: <span className="font-medium">{unit}</span>
          </p>
        ) : null}
      </div>
    );
  },
);

MonthlyTargetGrid.displayName = "MonthlyTargetGrid";

export default MonthlyTargetGrid;
